import { useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, fmtN, toParagraph, money, noPeriod, num, paragraphs, range, rangeText, stagger, stripHtml } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { Lightbox, captionOf, useLightbox } from '../utils/lightbox';

const photo1Def: SlotGroupDefinition = {
  label: 'Photo 1',
  description: 'Defaults to gallery photo 1. Empty photos are skipped in the carousel.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[0].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'The single photo that best introduces this property as a place to live: its exterior, grounds, a shared amenity space, or a furnished living area or kitchen. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context:
      'Slide 1 of up to 6 in the Home page "About the Community" carousel (large landscape photo beside the community description), shown first on load. The caption is a short pill on the photo naming what it shows. Do not reuse a photo from slides 2–6.',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[1].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'A second flattering photo of this property that shows a different view or space from slide 1. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 2 of up to 6 in the Home page "About the Community" carousel (large landscape photo, also a thumbnail under it). Different photo from every other slide.',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[2].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'A third flattering photo of this property, ideally an inside-the-home view if slides 1–2 are outdoors (or the reverse). Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 3 of up to 6 in the Home page "About the Community" carousel (large landscape photo, also a thumbnail under it). Different photo from every other slide.',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[3].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'Another unused, well-composed photo of this property worth showing a prospective renter. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 4 of up to 6 in the Home page "About the Community" carousel (large landscape photo, also the last thumbnail under it). Different photo from every other slide.',
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[4].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'Another unused, well-composed photo of this property, or leave empty if no remaining photo is as strong as slides 1–4. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 5 of up to 6 in the Home page "About the Community" carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
    title: { label: 'Caption', type: 'text', defaultField: 'photo_gallery[5].title' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' } },
  generator: {
    prompt:
      'A last distinct attractive photo of this property, or leave empty if none is left. Never a bathroom, toilet, closet, laundry or utility room, bare unfinished room, floor plan drawing, map, logo, or text graphic.',
    context: 'Slide 6 of 6 (the final slide) in the Home page "About the Community" carousel (large landscape photo). Optional; different photo from every other slide.',
  },
};

export function PropertyOverview() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const lb = useLightbox();
  const bodyRaw = useContentToken('propertyoverview.body');
  const groups = [
    useSlotGroup('propertyoverview.photo_1', photo1Def),
    useSlotGroup('propertyoverview.photo_2', photo2Def),
    useSlotGroup('propertyoverview.photo_3', photo3Def),
    useSlotGroup('propertyoverview.photo_4', photo4Def),
    useSlotGroup('propertyoverview.photo_5', photo5Def),
    useSlotGroup('propertyoverview.photo_6', photo6Def),
  ];
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const x0 = useRef<number | null>(null);

  /** Live, empty slots drop out of the carousel; in the editor they stay as placeholders so each slot can be filled. */
  const slides = groups
    .map((g, k) => {
      const alt = clean(g.alt) || '';
      return { slot: k, src: clean(g.image) || '', alt, caption: captionOf({ title: clean(g.title) || undefined, alt_text: alt }) };
    })
    .filter((s) => editing || s.src);
  const photos = slides.filter((s) => s.src);
  const n = slides.length;
  const cur = n ? slides[((slide % n) + n) % n] : null;
  const go = (d: number) => setSlide((s) => (s + d + n) % n);

  const layer = (k: number, node: ReactNode) => (
    <div
      key={k}
      className={`absolute inset-0 [transition:opacity_var(--q-motion-slow)_var(--q-ease-in-out)] ${cur?.slot === k ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      aria-hidden={cur?.slot === k ? undefined : true}
    >
      {node}
    </div>
  );
  const content = (k: number) => {
    const s = slides.find((x) => x.slot === k);
    if (!s) return null;
    if (s.src) return <VibesitesImage url={s.src} alt={s.alt} priority={k === 0} sizes="(min-width: 1200px) 50vw, 100vw" className="h-full w-full object-cover" />;
    return (
      <div className="flex h-full items-center justify-center border border-dashed border-[--q-border] text-sm text-[--q-muted]">
        Photo {k + 1} · empty, hidden on the live site
      </div>
    );
  };

  const paras = paragraphs(bodyRaw);
  const words = stripHtml(paras[0] || '').split(/\s+/).length;
  const expandable = paras.length > 1 || words > 75;
  const clampOnly = paras.length <= 1 && words > 75;

  const facts: Array<[string, string, string?, string?]> = [];
  if (p.property_bedrooms_range) facts.push(['Bedrooms', rangeText(p.property_bedrooms_range)]);
  if (p.property_bathrooms_range) facts.push(['Bathrooms', rangeText(p.property_bathrooms_range)]);
  if (p.property_sqft_range) {
    const [a, b] = String(p.property_sqft_range).split('-').map(num);
    facts.push(['Sq ft', range(a ?? null, b ?? null)]);
  }
  const price = num(p.property_price_min);
  if (price) facts.push(['Starting at', money(price)!, '/ month', 'text-[--q-secondary]']);
  if (p.property_ispetfriendly) {
    const kinds = [p.property_allows_dogs && 'dogs', p.property_allows_cats && 'cats'].filter(Boolean).join(' & ');
    facts.push(['Pet friendly', 'Yes', kinds]);
  }
  const homes = num(p.property_total_units);
  if (homes) facts.push(['Homes', fmtN(homes)]);

  return (
    <section className="section flex flex-wrap items-stretch justify-between gap-[56px_64px] max-[1199px]:flex-col max-[767px]:gap-6" id="overview">
      <div
        className="flex w-[592px] shrink-0 flex-col gap-7 max-[1199px]:w-auto max-[767px]:gap-6 min-[1200px]:w-auto min-[1200px]:min-w-0 min-[1200px]:max-w-[592px] min-[1200px]:flex-[1_1_0]"
        data-reveal
      >
        <RichTextContent path="propertyoverview.title" as="h2" className="h2" defaultValue={toParagraph(noPeriod(clean(p.property_home_hero_title) || ''))} />
        <RichTextContent
          path="propertyoverview.body"
          as="div"
          className={`richtext leading-[26px] text-[--q-muted] [&_p:first-child>strong:first-child]:hidden${expandable && !open && !clampOnly ? ' [&_p:nth-of-type(n+2)]:hidden' : ''}${clampOnly ? ` overflow-hidden [transition:max-height_var(--q-motion-slow)_var(--q-ease-in-out)] motion-reduce:[transition:none] ${open ? 'max-h-none [-webkit-mask-image:none] [mask-image:none]' : 'max-h-[182px] [-webkit-mask-image:linear-gradient(#000_60%,transparent)] [mask-image:linear-gradient(#000_60%,transparent)]'}` : ''}`}
        />
        {expandable && (
          <button
            className="-mt-3 inline-flex min-h-11 items-center gap-1.5 font-medium text-[--q-primary-on-paper] underline underline-offset-[3px] max-[767px]:mt-[-18px]"
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <RichTextContent path="propertyoverview.read_less" as="span" />
            ) : (
              <RichTextContent path="propertyoverview.read_more" as="span" />
            )}
            <svg
              className={`h-3 w-3 fill-none stroke-current stroke-[1.5] [stroke-linecap:round] [stroke-linejoin:round] [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] motion-reduce:[transition:none]${open ? ' [transform:rotate(180deg)]' : ''}`}
              viewBox="0 0 12 12"
              aria-hidden="true"
            >
              <path d="M2 4.5 6 8.5l4-4" />
            </svg>
          </button>
        )}
      </div>

      {cur && (
        <div className="min-w-0 flex-[1_1_0] max-[1199px]:w-full max-[767px]:order-[-1] max-[767px]:mx-[calc(-1*var(--gutter))] max-[767px]:my-0 max-[767px]:w-auto">
          <div className="flex min-w-0 grow flex-col gap-4 max-[767px]:order-[-1] min-[1200px]:flex-[1_1_0]" data-reveal style={stagger(1)}>
            <div
              className={`relative h-[470px] overflow-hidden rounded-md bg-[--q-surface] max-[1199px]:h-[420px] max-[767px]:h-[280px] max-[767px]:rounded-none${editing ? '' : ' cursor-zoom-in'}`}
              data-open="lightbox"
              onClick={(e) => {
                if (editing || !cur.src || (e.target as Element).closest('button')) return;
                lb.open(photos.indexOf(cur));
              }}
              onTouchStart={(e) => (x0.current = e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (x0.current == null) return;
                const dx = e.changedTouches[0].clientX - x0.current;
                if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
                x0.current = null;
              }}
            >
              {layer(0, <EditableSlotGroup groupPath="propertyoverview.photo_1" definition={photo1Def} className="h-full">{content(0)}</EditableSlotGroup>)}
              {layer(1, <EditableSlotGroup groupPath="propertyoverview.photo_2" definition={photo2Def} className="h-full">{content(1)}</EditableSlotGroup>)}
              {layer(2, <EditableSlotGroup groupPath="propertyoverview.photo_3" definition={photo3Def} className="h-full">{content(2)}</EditableSlotGroup>)}
              {layer(3, <EditableSlotGroup groupPath="propertyoverview.photo_4" definition={photo4Def} className="h-full">{content(3)}</EditableSlotGroup>)}
              {layer(4, <EditableSlotGroup groupPath="propertyoverview.photo_5" definition={photo5Def} className="h-full">{content(4)}</EditableSlotGroup>)}
              {layer(5, <EditableSlotGroup groupPath="propertyoverview.photo_6" definition={photo6Def} className="h-full">{content(5)}</EditableSlotGroup>)}
              {cur.src && cur.caption && <div className="pill pill--paper pointer-events-none absolute bottom-5 left-5">{cur.caption}</div>}
              {n > 1 && (
                <div className="absolute bottom-5 right-5 flex gap-2 max-[767px]:[&_.iconbtn]:h-11 max-[767px]:[&_.iconbtn]:w-11 max-[767px]:[&_.iconbtn]:text-[16px]">
                  <button className="iconbtn" type="button" aria-label="Previous photo" onClick={() => go(-1)}>←</button>
                  <button className="iconbtn" type="button" aria-label="Next photo" onClick={() => go(1)}>→</button>
                </div>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-3 [&_img]:h-full [&_img]:w-full [&_img]:object-cover max-[767px]:gap-2 max-[767px]:px-[--gutter] max-[767px]:py-0">
                {photos.slice(0, 4).map((ph, i) => (
                  <button
                    key={ph.slot}
                    type="button"
                    aria-label={`Photo ${i + 1}`}
                    className={`h-[92px] flex-[1_1_0] overflow-hidden rounded-sm bg-[--q-surface] outline outline-2 outline-offset-[-2px] [transition:outline-color_var(--q-motion-normal),opacity_var(--q-motion-normal)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] max-[767px]:h-14 ${ph === cur ? 'opacity-100 outline-[--q-primary-on-paper] focus-visible:outline-[--q-primary-on-paper]' : 'opacity-80 outline-transparent hover:opacity-100 focus-visible:outline-transparent'}`}
                    onClick={() => setSlide(slides.indexOf(ph))}
                  >
                    <VibesitesImage url={ph.src} alt="" sizes="160px" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {facts.length > 0 && (
        <div
          className="flex w-full basis-full border-y border-y-[--q-border] max-[1199px]:grid max-[1199px]:grid-cols-[repeat(3,1fr)] max-[1199px]:[border-bottom:0] max-[767px]:flex max-[767px]:grid-cols-[repeat(2,1fr)] max-[767px]:flex-col max-[767px]:gap-0"
          data-reveal
          style={stagger(2)}
        >
          {facts.map(([label, value, small, cls], i) => (
            <div
              className={`flex min-w-0 max-w-[calc(100%/3)] flex-[1_1_0] flex-col gap-1.5 py-4${i > 0 ? ' border-l border-l-[--q-border] pl-6' : ''} max-[1199px]:max-w-none max-[1199px]:border-b max-[1199px]:border-b-[--q-border]${i % 3 === 0 ? ' max-[1199px]:pl-0 max-[1199px]:[border-left:0]' : ''} max-[767px]:flex-row max-[767px]:items-baseline max-[767px]:justify-between max-[767px]:px-0 max-[767px]:py-3 max-[767px]:[border-left:0]`}
              key={label}
            >
              <div className="label">{label}</div>
              <div className={`flex items-baseline gap-1.5 whitespace-nowrap font-heading text-[28px] leading-[34px] max-[767px]:text-[18px] max-[767px]:leading-6 ${cls || ''}`}>
                {value}
                {small && <small className="font-body text-[13px] text-[--q-muted] max-[767px]:text-[12px]">{small}</small>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Lightbox items={photos} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'About the Community',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      description: 'Until edited, shows the home hero title without its closing period.',
      type: 'richtext',
      defaultValue: '<p>Welcome home</p>',
      suggestedFields: ['property_home_hero_title', 'property_home_romance_title'],
    },
    'propertyoverview.body': {
      label: 'Description',
      description: 'The first paragraph shows; Read more unfolds the rest.',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p><span data-type="field-token" data-field="property_short_description"></span></p>',
    },
    'propertyoverview.read_more': { label: 'Read More Text', type: 'richtext', defaultValue: '<p>Read more</p>' },
    'propertyoverview.read_less': { label: 'Read Less Text', type: 'richtext', defaultValue: '<p>Read less</p>' },
  },
  slotGroups: {
    'propertyoverview.photo_1': photo1Def,
    'propertyoverview.photo_2': photo2Def,
    'propertyoverview.photo_3': photo3Def,
    'propertyoverview.photo_4': photo4Def,
    'propertyoverview.photo_5': photo5Def,
    'propertyoverview.photo_6': photo6Def,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
